import {
  Address,
  AuctionItemSearchPayload,
  AuctionItemSell,
  AuctionService,
  ConfigurationState,
  LaunchDarklyService,
  DateUtilsService,
  DateTimeFormat,
  OfferList
} from 'remarketing-angular-library';
import { FileSaverOptions } from 'file-saver';
import { map, mergeMap } from 'rxjs/operators';
import { forkJoin, from, Observable } from 'rxjs';
import {
  difference as _difference,
  flatten as _flatten,
  get as _get,
  pick as _pick,
  omit as _omit
} from 'lodash';
import { Store } from '@ngxs/store';
import { stringify } from 'csv-stringify/browser/esm';
import { AuctionItemStatus } from 'remarketing-angular-library/interfaces/sell-auction-item';

interface SaveAsFunction {
  (data: Blob | string, filename?: string, options?: FileSaverOptions);
}

const enum keys {
  ODOMETER = 'odometer',
  EXTERIOR_GENERIC_COLOR = 'exteriorGenericColor',
  VDP_LINK = 'vdpLink',
  BUYER_FINANCE_SOURCE = 'buyerFinanceSource',
  CUSTOMER_ID = 'customerId',
  DEALERSHIP_PHONE = 'dealershipPhone',
  BUYER_EMAIL = 'buyerEmail'
}

const enum headers {
  ODOMETER = 'Odometer',
  SHORT_COLOR = 'Short Color',
  VEHICLE_DETAIL_PAGE = 'Vehicle Detail Page',
  BUYER_FINANCE_SOURCE = 'Buyer Finance Source',
  CUSTOMER_ID = 'Customer ID',
  DEALERSHIP_PHONE = 'Dealership Phone',
  BUYER_EMAIL = 'Buyer Email'
}

const activeStatus: AuctionItemStatus = 'ACTIVE';
const removalStatus: AuctionItemStatus = 'REMOVED';
const expiredStatus: AuctionItemStatus = 'EXPIRED';
const soldStatus: AuctionItemStatus = 'SOLD';
const holdStatus: AuctionItemStatus = 'HOLD';
const reservedStatus: AuctionItemStatus = 'RESERVED';
const DEFAULT_FILTER_STATUS ='DEFAULT';
const ISV_VEHICLE_TYPE = 'ISV';
const ACP_VEHICLE_TYPE = 'ACP';

export class InventoryExportService {

  static readonly EXPORT_CHUNK_SIZE = 300;
  timezone: string;
  private finalColumns = [];
  columnsToFilter: Array<string> = [];
  private readonly holdColumns = [
    { key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    { key: 'holdDate', header: 'Hold Date' },
    { key: 'holdTime', header: 'Hold Time' },
    { key: 'holdStatusReason', header: 'Hold Reason' },
    { key: 'holdDays', header: 'Days on Hold' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn()
  ];

  private readonly removedColumns = [
    { key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'auctionTenant', header: 'Tenant Badge' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    { key: 'postedDate', header: 'Posted Date' },
    { key: 'postedTime', header: 'Posted Time' },
    { key: 'removalDate', header: 'Removal Date' },
    { key: 'removalTime', header: 'Removal Time' },
    { key: 'removalCount', header: 'Removal Count' },
    { key: 'removalStatusReason', header: 'Removal Reason' },
    { key: 'bidListAdds', header: 'Bid List Adds' },
    { key: 'highestOffer', header: 'Highest Offer Price' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn()
  ];

  private readonly activeColumns = [
    { key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'auctionEndTime', header: 'Auction End Date' },
    { key: 'auctionStartTime', header: 'Auction Start Date' },
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'averageSalesPrice', header: 'Average Sales Price' },
    { key: 'daysRemainingInAuction', header: 'Days Remaining' },
    { key: 'auctionTenant', header: 'Tenant Badge' },
    { key: 'imageCount', header: 'Image Count' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn()
  ];

  private readonly reservedColumns = [
    { key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'auctionEndTime', header: 'Auction End Date' },
    { key: 'auctionStartTime', header: 'Auction Start Date' },
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'averageSalesPrice', header: 'Average Sales Price' },
    { key: 'daysRemainingInAuction', header: 'Days Remaining' },
    { key: 'auctionTenant', header: 'Tenant Badge' },
    { key: 'imageCount', header: 'Image Count' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn(),
    this.getReservationDateColumn(),
    this.getReservedByColumn(),
    this.getInvoiceDateColumn(),
    this.getInvoiceStatusColumn()
  ];

  private readonly soldColumns = [{ key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'auctionEndTime', header: 'Auction End Date' },
    { key: 'auctionStartTime', header: 'Auction Start Date' },
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'daysRemainingInAuction', header: 'Days Remaining' },
    { key: 'auctionTenant', header: 'Tenant Badge' },
    { key: 'imageCount', header: 'Image Count' },
    { key: 'saleDate', header: 'Sale Date' },
    { key: 'saleTime', header: 'Sale Time' },
    { key: 'salePrice', header: 'Sale Price' },
    { key: 'buyerName', header: 'Buyer Name' },
    { key: 'buyerAddress', header: 'Buyer Address' },
    { key: 'buyerCity', header: 'Buyer City' },
    { key: 'buyerState', header: 'Buyer State' },
    { key: 'buyerZip', header: 'Buyer Zip' },
    this.getDealershipPhone(),
    this.getBuyerEmail(),
    { key: 'buyerPhone', header: 'Buyer Phone' },
    this.getBuyerFinanceSource(),
    this.getCustomerId(),
    { key: 'titleName', header: 'Title Name' },
    { key: 'titleAddress1', header: 'Title Address 1' },
    { key: 'titleAddress2', header: 'Title Address 2' },
    { key: 'titleCity', header: 'Title City' },
    { key: 'titleState', header: 'Title State' },
    { key: 'titleZip', header: 'Title Zip' },
    { key: 'settlementDate', header: 'Settlement Date' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn(),
    this.getSoldToRepColumn(),
    this.getReservationDateColumn(),
    this.getReservedByColumn(),
    this.getInvoiceDateColumn(),
    this.getInvoiceStatusColumn()
  ];

  private readonly allColumns = [
    { key: 'vin', header: 'VIN' },
    { key: 'modelYear', header: 'Year' },
    { key: 'make', header: 'Make' },
    { key: 'model', header: 'Model' },
    { key: 'trim', header: 'Trim' },
    { key: 'driveTrain', header: 'Drivetrain' },
    this.getOdometer(),
    this.getExteriorGenericColor(),
    this.getStockOrMvaHeader(),
    { key: 'auctionEndTime', header: 'Auction End Date' },
    { key: 'auctionStartTime', header: 'Auction Start Date' },
    { key: 'location', header: 'Location' },
    { key: 'viewCount', header: 'Hits' },
    { key: 'buyNowAmount', header: 'Buy Now' },
    { key: 'openingPrice', header: 'Opening Price' },
    { key: 'averageSalesPrice', header: 'Average Sales Price' },
    { key: 'daysRemainingInAuction', header: 'Days Remaining' },
    { key: 'auctionTenant', header: 'Tenant Badge' },
    { key: 'imageCount', header: 'Image Count' },
    { key: 'saleDate', header: 'Sale Date' },
    { key: 'saleTime', header: 'Sale Time' },
    { key: 'salePrice', header: 'Sale Price' },
    { key: 'buyerName', header: 'Buyer Name' },
    { key: 'buyerAddress', header: 'Buyer Address' },
    { key: 'buyerCity', header: 'Buyer City' },
    { key: 'buyerState', header: 'Buyer State' },
    { key: 'buyerZip', header: 'Buyer Zip' },
    { key: 'buyerPhone', header: 'Buyer Phone' },
    this.getBuyerFinanceSource(),
    this.getCustomerId(),
    { key: 'titleName', header: 'Title Name' },
    { key: 'titleAddress1', header: 'Title Address 1' },
    { key: 'titleAddress2', header: 'Title Address 2' },
    { key: 'titleCity', header: 'Title City' },
    { key: 'titleState', header: 'Title State' },
    { key: 'titleZip', header: 'Title Zip' },
    { key: 'settlementDate', header: 'Settlement Date' },
    { key: 'buyFee', header: 'Service Fee' },
    { key: 'saleFee', header: 'Sale Fee' },
    { key: 'sellerName', header: 'Seller' },
    { key: 'postedDate', header: 'Posted Date' },
    { key: 'postedTime', header: 'Posted Time' },
    { key: 'removalDate', header: 'Removal Date' },
    { key: 'removalTime', header: 'Removal Time' },
    { key: 'removalCount', header: 'Removal Count' },
    { key: 'removalStatusReason', header: 'Removal Reason' },
    { key: 'bidListAdds', header: 'Bid List Adds' },
    { key: 'highestOffer', header: 'Highest Offer Price' },
    { key: 'holdDate', header: 'Hold Date' },
    { key: 'holdTime', header: 'Hold Time' },
    { key: 'holdStatusReason', header: 'Hold Reason' },
    { key: 'holdDays', header: 'Days on Hold' },
    this.getVehicleDetailPage(),
    this.getAuctionItemStatusColumn(),
    this.getSoldToRepColumn(),
    this.getReservationDateColumn(),
    this.getReservedByColumn(),
    this.getInvoiceDateColumn(),
    this.getInvoiceStatusColumn()
  ];

  getAuctionItemStatusColumn() {
    return this.isIsvDetailsEnabled ? {key: 'auctionItemStatus', header: 'Status of Vehicle'} : '';
  }

  getReservationDateColumn() {
    return this.isIsvDetailsEnabled ? {key: 'reservedAt', header: 'Reservation Date'} : '';
  }

  getReservedByColumn() {
    //NOTE that Avis Rep Name is used for multiple column headers but with different column keys
    return this.isIsvDetailsEnabled ? {key: 'reservedByUserName', header: 'Avis Rep Name'} : '';
  }

  getSoldToRepColumn() {
    //NOTE that Avis Rep Name is used for multiple column headers but with different column keys
    return this.isAcpDetailsEnabled ? {key: 'soldToRepName', header: 'Avis Rep Name'} : '';
  }

  getInvoiceDateColumn() {
    return this.isIsvDetailsEnabled ? {key: 'invoiceDate', header: 'Invoice Date'} : '';
  }

  getInvoiceStatusColumn() {
    return this.isIsvDetailsEnabled ? {key: 'invoiceStatus', header: 'Invoice Status'} : '';
  }

  constructor(
    private readonly saveAs: SaveAsFunction,
    public auctionService: AuctionService,
    private readonly store: Store,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly dateUtilsService: DateUtilsService
  ) { }

  setColumnsToFilter(filterByStatus: string, filterByInService: boolean, isFilterByInvoice: boolean) {
    this.columnsToFilter = [];
    if(this.isIsvDetailsEnabled || this.isAcpDetailsEnabled) {
      this.handleColumnFilteringOnVehicleStatusAndInServiceStatus(filterByStatus, filterByInService, isFilterByInvoice);
    } else {
      this.setAllIsvAndAcpRelatedColumnsToBeFilteredOut();
      this.handleColumnFilteringByVehicleStatus(filterByStatus);
    }
  }

  handleColumnFilteringOnVehicleStatusAndInServiceStatus(filterByStatus: string, filterByInService: boolean, isFilterByInvoice: boolean) : void {
    if(this.isIsvDetailsEnabled || this.isAcpDetailsEnabled) {
      if (filterByInService && this.isIsvDetailsEnabled) {
        this.executeIsvColumnFiltering(filterByStatus, isFilterByInvoice);
      } else if (!filterByInService && this.isAcpDetailsEnabled) {
        this.executeAcpColumnFiltering(filterByStatus);
      } else {
        this.executeGenericFilteringFlow(filterByStatus);
      }
    } else {
      this.executeGenericFilteringFlow(filterByStatus);
    }
  }

  executeGenericFilteringFlow(filterByStatus: string): void {
    this.setAllIsvAndAcpRelatedColumnsToBeFilteredOut();
    this.handleColumnFilteringByVehicleStatus(filterByStatus);
  }

  executeIsvColumnFiltering(filterByStatus: string, isFilterByInvoice: boolean): void {
    this.setAllAcpSpecificColumnsFilteredOut();
    if(!this.isShowReservationColumnsOnExportCsv(filterByStatus)) {
      this.setAllReservationColumnsToBeFilteredOut();
    }
    if(!this.isShowInvoiceColumnsOnExportCsv(filterByStatus, isFilterByInvoice)) {
      this.setAllInvoiceColumnsToBeFilteredOut();
    }
    this.handleColumnFilteringByVehicleStatus(filterByStatus);
  }

  executeAcpColumnFiltering(filterByStatus: string) {
    this.setAllIsvSpecificColumnsFilteredOut();
    if(filterByStatus !== DEFAULT_FILTER_STATUS && filterByStatus !== soldStatus.toString()) {
      this.setSoldToRepNameColumnToBeFilteredOut();
    }
    this.handleColumnFilteringByVehicleStatus(filterByStatus);
  }

  isShowReservationColumnsOnExportCsv(filterByStatus) : boolean {
    return filterByStatus === soldStatus || filterByStatus === reservedStatus || filterByStatus === DEFAULT_FILTER_STATUS;
  }

  isShowInvoiceColumnsOnExportCsv(filterByStatus: string, isFilterByInvoice: boolean): boolean {
    return true === isFilterByInvoice || filterByStatus === DEFAULT_FILTER_STATUS || filterByStatus === reservedStatus;
  }

  handleColumnFilteringByVehicleStatus(filterByStatus) {
    switch (filterByStatus) {
    case holdStatus:
      break;

    case removalStatus:
    case expiredStatus:
    case soldStatus:
      this.handleTenantBadgeColumnFilteringByLdFlag();
      break;

    case reservedStatus:
    case activeStatus:
    default:
      this.handleTenantBadgeColumnFilteringByLdFlag();
      this.handleAverageSalePriceColumnFilteringByLdFlag();
      break;
    }
  }

  setAllIsvAndAcpRelatedColumnsToBeFilteredOut(): void {
    this.setVehicleStatusColumnToBeFilteredOut();
    this.setAllIsvSpecificColumnsFilteredOut();
    this.setAllAcpSpecificColumnsFilteredOut();
  }

  setVehicleStatusColumnToBeFilteredOut(): void {
    this.columnsToFilter.push('auctionItemStatus');
  }

  setAllReservationColumnsToBeFilteredOut(): void {
    this.columnsToFilter.push('reservedAt');
    this.columnsToFilter.push('reservedByUserName');
  }

  setAllIsvSpecificColumnsFilteredOut() : void {
    this.setAllInvoiceColumnsToBeFilteredOut();
    this.setAllReservationColumnsToBeFilteredOut();
  }

  setAllAcpSpecificColumnsFilteredOut(): void {
    this.setSoldToRepNameColumnToBeFilteredOut();
  }

  setSoldToRepNameColumnToBeFilteredOut(): void {
    this.columnsToFilter.push('soldToRepName');
  }

  setAllInvoiceColumnsToBeFilteredOut(): void {
    this.columnsToFilter.push('invoiceDate');
    this.columnsToFilter.push('invoiceStatus');
  }

  handleTenantBadgeColumnFilteringByLdFlag() {
    const isTenantBadgeEnabled = this.launchDarklyService.getFeatureFlag('sell-tenantBadgeEnabled', false);
    if (!isTenantBadgeEnabled) {
      this.columnsToFilter.push('auctionTenant');
    }
  }

  handleAverageSalePriceColumnFilteringByLdFlag() {
    const isSellUpdateInventoryEnabled = this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
    if (!isSellUpdateInventoryEnabled) {
      this.columnsToFilter.push('averageSalesPrice');
    }
  }

  private static getBuyerDetails(item: AuctionItemSell) {
    const { oeName: buyerName, mailingAddress } = _pick(item.buyerDetails, ['oeName', 'mailingAddress']);
    const {
      city: buyerCity,
      state: buyerState,
      zip: buyerZip,
      phone: buyerPhone,
      addressLine1 = '',
      addressLine2 = ''
    } = _pick(mailingAddress, ['city', 'state', 'zip', 'phone', 'addressLine1', 'addressLine2']);
    const buyerAddress = `${addressLine1}${addressLine2 ? ` ${addressLine2}` : ''}`;
    const dealershipPhone = _get(item.buyerDetails, 'primaryPhone', '');
    const buyerEmail = _get(item.buyerDetails?.contactDetails?.buyer, 'email', '');
    return { buyerName, buyerCity, buyerState, buyerZip, buyerPhone, buyerAddress, dealershipPhone, buyerEmail };
  }

  private static getTitleDetails(item: AuctionItemSell) {
    const titleDetails = _get(item, 'titleAddress', {}) as Address;
    const {
      name: titleName,
      addressLine1: titleAddress1,
      addressLine2: titleAddress2,
      city: titleCity,
      state: titleState,
      zip: titleZip
    } = _pick(titleDetails, ['name', 'addressLine1', 'addressLine2', 'city', 'state', 'zip']);

    return {
      titleName,
      titleAddress1,
      titleAddress2,
      titleCity,
      titleState,
      titleZip
    };
  }

  private static getCustomerInfo(item: AuctionItemSell){
    const buyerFinanceSource = _get(item, 'buyerFinanceSource', '');
    const customerId = _get(item, 'customerId', '');
    return { buyerFinanceSource, customerId };
  }

  isExportAdditionalFields(): boolean {
    return this.launchDarklyService.getFeatureFlag('inventory-export-additionalFieldsEnabled', false);
  }

  get isIsvDetailsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-export-isvDetailsEnabled', false);
  }

  get isAcpDetailsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-export-acpDetailsEnabled', false);
  }

  get isMvaColumnLabel(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-export-mvaHeaderEnabled', false);
  }

  isCustomerInfoEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-customerInfoEnabled', false);
  }

  isBuyerDetailsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('inventory-export-buyerDetailsEnabled', false);
  }

  getOdometer() {
    if (this.isExportAdditionalFields()) {
      return { key: keys.ODOMETER, header: headers.ODOMETER };
    } else {
      return '';
    }
  }

  getExteriorGenericColor() {
    if (this.isExportAdditionalFields()) {
      return { key: keys.EXTERIOR_GENERIC_COLOR, header: headers.SHORT_COLOR };
    } else {
      return '';
    }
  }

  getVehicleDetailPage() {
    if (this.isExportAdditionalFields()) {
      return { key: keys.VDP_LINK, header: headers.VEHICLE_DETAIL_PAGE };
    } else {
      return '';
    }
  }

  getStockOrMvaHeader(): object {
    return this.isMvaColumnLabel ? { key: 'stockNumber', header: 'MVA' } : { key: 'stockNumber', header: 'STOCK/UNIT #' };
  }

  getDealershipPhone() {
    if (this.isBuyerDetailsEnabled()) {
      return { key: keys.DEALERSHIP_PHONE, header: headers.DEALERSHIP_PHONE };
    } else {
      return '';
    }
  }

  getBuyerEmail() {
    if (this.isBuyerDetailsEnabled()) {
      return { key: keys.BUYER_EMAIL, header: headers.BUYER_EMAIL };
    } else {
      return '';
    }
  }

  getBuyerFinanceSource(){
    if (this.isCustomerInfoEnabled()) {
      return { key: keys.BUYER_FINANCE_SOURCE, header: headers.BUYER_FINANCE_SOURCE };
    } else {
      return '';
    }
  }

  getCustomerId(){
    if (this.isCustomerInfoEnabled()) {
      return { key: keys.CUSTOMER_ID, header: headers.CUSTOMER_ID };
    } else {
      return '';
    }
  }

  private getDaysRemainingInAuction(item: AuctionItemSell) {
    const { daysRemainingInAuction, auctionItemStatus } = item;
    if (auctionItemStatus === activeStatus) {
      return daysRemainingInAuction;
    }
    return undefined;
  }

  private getBuyNowAmount(item: AuctionItemSell) {
    const { buyNowAmount, buyNowAllowed } = item;
    if (buyNowAllowed) {
      return buyNowAmount;
    }
    return undefined;
  }

  private getSaleDateTime(item: AuctionItemSell) {
    const saleDate = _get(item, 'saleDate');
    if (!saleDate) {
      return { saleDate: '', saleTime: '' };
    }
    return {
      saleDate: this.dateUtilsService.formatDate(saleDate, DateTimeFormat.MM_DD_YYYY, false),
      saleTime: this.dateUtilsService.formatDate(saleDate, DateTimeFormat.H_MM_A, true)
    };
  }

  private getHoldDateTime(item: AuctionItemSell) {
    const { statusChangedDate, auctionItemStatus } = item;
    if (!statusChangedDate || auctionItemStatus !== holdStatus) {
      return { holdDate: '', holdTime: '' };
    }
    return {
      holdDate: this.dateUtilsService.formatDate(statusChangedDate, DateTimeFormat.MM_DD_YYYY, false),
      holdTime: this.dateUtilsService.formatDate(statusChangedDate, DateTimeFormat.H_MM_A, true)
    };
  }

  private isRemoved(auctionItemStatus: AuctionItemStatus) {
    return [removalStatus, expiredStatus].includes(auctionItemStatus);
  }

  private getRemovalDateTime(item: AuctionItemSell) {
    const { statusChangedDate, auctionItemStatus } = item;
    if (!statusChangedDate || !this.isRemoved(auctionItemStatus)) {
      return { removalDate: '', removalTime: '' };
    }
    return {
      removalDate: this.dateUtilsService.formatDate(statusChangedDate, DateTimeFormat.MM_DD_YYYY, false),
      removalTime: this.dateUtilsService.formatDate(statusChangedDate, DateTimeFormat.H_MM_A, true)
    };
  }

  private getRemovalStatusReason(item: AuctionItemSell) {
    const { itemStatusReasons, auctionItemStatus } = item;
    if (this.isRemoved(auctionItemStatus) && itemStatusReasons && itemStatusReasons.length > 0) {
      const reason = itemStatusReasons[0].reason;
      const updatedBy = itemStatusReasons[0].updatedBy;
      return { removalStatusReason: `${reason} (${updatedBy})` };
    } else {
      return { removalStatusReason: '' };
    }
  }

  private getHoldDays(item: AuctionItemSell) {
    const { holdDays, auctionItemStatus } = item;
    if (auctionItemStatus !== holdStatus) {
      return '';
    }
    return holdDays;
  }

  private getHoldStatusReason(item: AuctionItemSell) {
    const { itemStatusReasons, auctionItemStatus } = item;
    if (auctionItemStatus === holdStatus && itemStatusReasons && itemStatusReasons.length > 0) {
      const reason = itemStatusReasons[0].reason;
      const updatedBy = itemStatusReasons[0].updatedBy;
      return { holdStatusReason: `${reason} (${updatedBy})` };
    }
    return { holdStatusReason: '' };
  }

  private getRepostedDateTime(repostedDate) {
    if (repostedDate) {
      return {
        repostedDate: this.dateUtilsService.formatDate(repostedDate, DateTimeFormat.MM_DD_YYYY, false),
        repostedTime: this.dateUtilsService.formatDate(repostedDate, DateTimeFormat.H_MM_A, true)
      };
    } else {
      return { repostedDate: '', repostedTime: '' };
    }
  }

  private vehicleColorNA(exteriorGenericColor) {
    if (exteriorGenericColor == 'N/A') {
      return '';
    } else {
      return exteriorGenericColor;
    }
  }

  private getPostedDateTime(postedDate) {
    if (postedDate) {
      return {
        postedDate: this.dateUtilsService.formatDate(postedDate, DateTimeFormat.MM_DD_YYYY, false),
        postedTime: this.dateUtilsService.formatDate(postedDate, DateTimeFormat.H_MM_A, true)
      };
    } else {
      return { postedDate: '', postedDateTime: '' };
    }
  }

  /**
   * Export the desired amount of inventory items by making multiple search requests of up to EXPORT_CHUNK_SIZE items
   * per page. Chunking is required due to backend limitations.
   */
  exportInventoryResults(
    initialRequest: AuctionItemSearchPayload,
    sellSearchExportLimit: number,
    totalItems: number
  ): Observable<string> {
    // How many pages at EXPORT_CHUNK_SIZE we need to reach the limit
    const maxPages = Math.ceil(sellSearchExportLimit / InventoryExportService.EXPORT_CHUNK_SIZE);
    /*
    The total number of pages we should fetch, comparing the max amount of pages and the total pages we could
    fetch given the chunk size and number of items. We need to add 1 otherwise nothing would export if total items
    was less than EXPORT_CHUNK_SIZE
    */
    const pagesToFetch = Math.min(
      maxPages,
      Math.floor(totalItems / InventoryExportService.EXPORT_CHUNK_SIZE) + 1
    );

    const createRequest = (page: number) => this.auctionService.searchAuctionItems({
      ...initialRequest,
      page,
      itemsPerPage: InventoryExportService.EXPORT_CHUNK_SIZE
    }).pipe(map(res => res.data));

    const requests = [];

    for (let i = 0; i < pagesToFetch; i++) {
      requests.push(createRequest(i));
    }

    // forkJoin allows all of the requests to run in parallel, similar to Promise.all
    return forkJoin(requests).pipe(mergeMap(pages => {
      let allRecords: any = _flatten(pages);
      if (allRecords.length > sellSearchExportLimit) {
        allRecords = allRecords.slice(0, sellSearchExportLimit);
      }
      return from(this.exportCsv(allRecords, initialRequest));
    }));
  }

  exportCsv(auctionItems: AuctionItemSell[], initialRequest: AuctionItemSearchPayload): Promise<string> {
    const filterByStatus = (initialRequest?.filterByStatus !== null && initialRequest?.filterByStatus !== undefined) ? initialRequest.filterByStatus : DEFAULT_FILTER_STATUS;
    const filterByInService = (initialRequest?.filterByInService !== null && initialRequest?.filterByInService !== undefined) ? initialRequest.filterByInService : false;

    // Explicitly setting filterByInvoice for column filtering because it doesn't come with every initial payload and can be null within the payload
    const filterByInvoice = initialRequest?.filterByOfferLists === true ? true : false;

    this.setColumnsToFilter(filterByStatus, filterByInService, filterByInvoice);
    this.timezone = _get(this.store.selectSnapshot(ConfigurationState.configuration), 'system.timeZone', 'US/Eastern');
    return new Promise((resolve, reject) => {
      const rows = auctionItems.map(item => this.mapToRow(item, filterByStatus));
      this.filterColumnsBasedOnPermissions();
      stringify(rows, { columns: this.finalColumns, header: true }, (err, data: string) => {
        if (err) {
          reject(err);
        } else {
          this.saveAs(new Blob([data]), 'InventoryResults.csv');
          resolve(data);
        }
      });
    });
  }

  private mapToRow(item: AuctionItemSell, filterByStatus) {
    const defaults = this.getExportFileDefaults(filterByStatus);
    const values = this.getExportFileValues(item, filterByStatus);

    this.assertColumnsSet(Object.keys(defaults), `Please check that all columns are accounted for in the ${filterByStatus} defaults.`);
    this.assertColumnsSet(Object.keys(values), `Please check that all columns are accounted for in the ${filterByStatus} values.`);

    return this.filterRowBasedOnPermissions(defaults, values);
  }

  private getExportFileDefaults(filterByStatus: string) {
    let defaults;
    switch (filterByStatus) {
    case holdStatus:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        buyFee: '',
        saleFee: '',
        sellerName: '',
        holdDate: '',
        holdTime: '',
        holdStatusReason: '',
        holdDays: '',
        vdpLink: '',
        auctionItemStatus: ''
      };
      this.finalColumns = this.holdColumns.filter(function (el) {
        return el !== '';
      });
      break;

    case removalStatus:
    case expiredStatus:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        auctionTenant: '',
        buyFee: '',
        saleFee: '',
        sellerName: '',
        postedDate: '',
        postedTime: '',
        removalCount: '',
        removalStatusReason: '',
        removalDate: '',
        removalTime: '',
        bidListAdds: 0,
        highestOffer: '',
        vdpLink: '',
        auctionItemStatus: ''
      };
      this.finalColumns = this.removedColumns.filter(function (el) {
        return el !== '';
      });
      break;

    case activeStatus:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        auctionEndTime: '',
        auctionStartTime: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        averageSalesPrice: '',
        daysRemainingInAuction: undefined,
        auctionTenant: '',
        imageCount: 0,
        buyFee: '',
        saleFee: '',
        sellerName: '',
        vdpLink: '',
        auctionItemStatus: ''
      };
      this.finalColumns = this.activeColumns.filter(function (el) {
        return el !== '';
      });

      break;

    case reservedStatus:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        auctionEndTime: '',
        auctionStartTime: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        averageSalesPrice: '',
        daysRemainingInAuction: undefined,
        auctionTenant: '',
        imageCount: 0,
        buyFee: '',
        saleFee: '',
        sellerName: '',
        vdpLink: '',
        auctionItemStatus: '',
        reservedAt: '',
        reservedByUserName: '',
        invoiceDate: '',
        invoiceStatus: ''
      };
      this.finalColumns = this.reservedColumns.filter(function (el) {
        return el !== '';
      });

      break;

    case soldStatus:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        auctionEndTime: '',
        auctionStartTime: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        daysRemainingInAuction: undefined,
        auctionTenant: '',
        imageCount: 0,
        saleDate: '',
        saleTime: '',
        salePrice: '',
        buyerName: '',
        buyerAddress: '',
        buyerCity: '',
        buyerState: '',
        buyerZip: '',
        buyerPhone: '',
        buyerFinanceSource: '',
        customerId: '',
        titleName: '',
        titleAddress1: '',
        titleAddress2: '',
        titleCity: '',
        titleState: '',
        titleZip: '',
        settlementDate: '',
        buyFee: '',
        saleFee: '',
        sellerName: '',
        vdpLink: '',
        auctionItemStatus: '',
        reservedAt: '',
        reservedByUserName: '',
        invoiceDate: '',
        invoiceStatus: '',
        soldToRepName: ''
      };
      this.finalColumns = this.soldColumns.filter(function (el) {
        return el !== '';
      });
      break;

    default:
      defaults = {
        vin: '',
        modelYear: undefined,
        make: '',
        model: '',
        trim: '',
        driveTrain: '',
        odometer: '',
        exteriorGenericColor: '',
        stockNumber: '',
        auctionEndTime: '',
        auctionStartTime: '',
        location: '',
        viewCount: 0,
        buyNowAmount: undefined,
        openingPrice: undefined,
        averageSalesPrice: '',
        daysRemainingInAuction: undefined,
        auctionTenant: '',
        imageCount: 0,
        saleDate: '',
        saleTime: '',
        salePrice: '',
        buyerName: '',
        buyerAddress: '',
        buyerCity: '',
        buyerState: '',
        buyerZip: '',
        buyerPhone: '',
        buyerFinanceSource: '',
        customerId: '',
        titleName: '',
        titleAddress1: '',
        titleAddress2: '',
        titleCity: '',
        titleState: '',
        titleZip: '',
        settlementDate: '',
        buyFee: '',
        saleFee: '',
        sellerName: '',
        postedDate: '',
        postedTime: '',
        repostedDate: '',
        repostedTime: '',
        removalCount: '',
        removalDate: '',
        removalTime: '',
        removalStatusReason: '',
        bidListAdds: 0,
        highestOffer: '',
        holdDate: '',
        holdTime: '',
        holdStatusReason: '',
        holdDays: '',
        vdpLink: '',
        auctionItemStatus: '',
        reservedAt: '',
        reservedByUserName: '',
        invoiceDate: '',
        invoiceStatus: '',
        soldToRepName: ''
      };
      this.finalColumns = this.allColumns.filter(function (el) {
        return el !== '';
      });
      break;
    }
    return defaults;
  }

  private getExportFileValues(item: AuctionItemSell, filterByStatus: string) {
    const {
      currentAmount,
      auctionEndTime,
      auctionStartTime,
      viewCount,
      daysRemainingInAuction,
      auctionTenant,
      imageCount,
      salePrice,
      settlementDate,
      buyFee,
      saleFee,
      auctionItemStatus,
      repostedDate,
      postedDate,
      removalCount,
      bidListAdds,
      highestOffer,
      averageSalesPrice,
      exteriorGenericColor,
      inventoryItemUuid
    } = item;

    const { vin, modelYear, make, odometer, model, trim, powerTrain, stockNumber } = _pick(item.itemDetail, [
      'vin',
      'modelYear',
      'make',
      'odometer',
      'model',
      'trim',
      'powerTrain',
      'stockNumber'
    ]);

    const { city = '', state = '' } = _pick(item.storageLocation, ['city', 'state']);
    const location = city + (city && state ? ', ' : '') + state;

    const { name: sellerName } = _pick(item.seller, ['name']);
    let values;
    switch (filterByStatus) {
    case holdStatus:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        buyFee,
        saleFee,
        sellerName,
        ...this.getHoldDateTime(item),
        ...this.getHoldStatusReason(item),
        holdDays: this.getHoldDays(item),
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        ...this.populateIsvAndAcpData(item)
      };
      break;

    case removalStatus:
    case expiredStatus:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        auctionTenant,
        buyFee,
        saleFee,
        sellerName,
        ...this.getPostedDateTime(postedDate),
        ...this.getRemovalDateTime(item),
        removalCount,
        ...this.getRemovalStatusReason(item),
        bidListAdds,
        highestOffer,
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        ...this.populateIsvAndAcpData(item)
      };
      break;

    case reservedStatus:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        averageSalesPrice,
        daysRemainingInAuction: this.getDaysRemainingInAuction(item),
        auctionTenant,
        imageCount,
        buyFee,
        saleFee,
        sellerName,
        auctionEndTime: this.dateUtilsService.formatDate(auctionEndTime, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        auctionStartTime: this.dateUtilsService.formatDate(auctionStartTime, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        ...this.populateIsvAndAcpData(item)
      };
      break;

    case activeStatus:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        averageSalesPrice,
        daysRemainingInAuction: this.getDaysRemainingInAuction(item),
        auctionTenant,
        imageCount,
        buyFee,
        saleFee,
        sellerName,
        auctionEndTime: this.dateUtilsService.formatDate(auctionEndTime, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        auctionStartTime: this.dateUtilsService.formatDate(auctionStartTime, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        ...this.populateIsvAndAcpData(item)
      };
      break;

    case soldStatus:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        auctionTenant,
        imageCount,
        ...this.getSaleDateTime(item),
        salePrice,
        ...InventoryExportService.getBuyerDetails(item),
        ...InventoryExportService.getCustomerInfo(item),
        ...InventoryExportService.getTitleDetails(item),
        settlementDate: this.dateUtilsService.formatDate(settlementDate, DateTimeFormat.MM_DD_YY_H_MM_A, true),
        buyFee,
        saleFee,
        sellerName,
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        auctionEndTime: this.setAuctionTime(auctionEndTime, auctionItemStatus),
        auctionStartTime: this.setAuctionTime(auctionStartTime, auctionItemStatus),
        daysRemainingInAuction: auctionItemStatus === 'SOLD' ? undefined : daysRemainingInAuction,
        ...this.populateIsvAndAcpData(item)
      };
      break;

    default:
      values = {
        vin,
        modelYear,
        make,
        model,
        trim,
        driveTrain: _get(powerTrain, 'driveTrainAbbrev', ''),
        odometer,
        exteriorGenericColor: this.vehicleColorNA(exteriorGenericColor),
        stockNumber,
        location,
        viewCount,
        buyNowAmount: this.getBuyNowAmount(item),
        openingPrice: currentAmount,
        averageSalesPrice,
        auctionTenant,
        imageCount,
        ...this.getSaleDateTime(item),
        salePrice,
        ...InventoryExportService.getBuyerDetails(item),
        ...InventoryExportService.getCustomerInfo(item),
        ...InventoryExportService.getTitleDetails(item),
        settlementDate: this.dateUtilsService.formatDate(settlementDate, DateTimeFormat.MM_DD_YY_H_MM_A, true),
        buyFee,
        saleFee,
        sellerName,
        auctionEndTime: this.dateUtilsService.formatDate(auctionEndTime, DateTimeFormat.MM_DD_YYYY_H_MM_A, true),
        auctionStartTime: this.dateUtilsService.formatDate(auctionStartTime, DateTimeFormat.MM_DD_YYYY_H_MM_A, true),
        daysRemainingInAuction: this.getDaysRemainingInAuction(item),
        ...this.getPostedDateTime(postedDate),
        ...this.getRepostedDateTime(repostedDate),
        ...this.getRemovalDateTime(item),
        ...this.getRemovalStatusReason(item),
        removalCount,
        bidListAdds,
        highestOffer,
        ...this.getHoldDateTime(item),
        ...this.getHoldStatusReason(item),
        holdDays: this.getHoldDays(item),
        vdpLink: this.vehicleInventoryLink(inventoryItemUuid),
        ...this.populateIsvAndAcpData(item)
      };
      break;
    }
    return values;
  }

  populateIsvAndAcpData(item: AuctionItemSell): object {
    if(this.isIsvDetailsEnabled && this.isIsvVehicle(item)) {
      return this.buildIsvColumnValues(item);
    } else if (this.isAcpDetailsEnabled && this.isAcpVehicle(item)) {
      return this.buildAcpColumnValues(item);
    } else {
      if(this.isIsvDetailsEnabled || this.isAcpDetailsEnabled) {
        return this.populateGenericTeamRedColumns(item);
      } else {
        return {};
      }
    }
  }

  buildIsvColumnValues(item: AuctionItemSell) {
    if(this.isIsvAndReservedSoldOrInvoiced(item)) {
      const offerListValuesSanitized: OfferListExportValues = this.getMostRecentInvoiceValuesFromAuctionItem(item);
      const reservationValuesSanitized: ReservationExportValues = this.getSanitizedReservationValues(item);
      return {
        auctionItemStatus: item.auctionItemStatus,
        reservedByUserName: reservationValuesSanitized.reservedByUserName,
        reservedAt: this.dateUtilsService.formatDate(reservationValuesSanitized.reservedAt, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        invoiceDate: this.dateUtilsService.formatDate(offerListValuesSanitized.sentDate, DateTimeFormat.M_D_YYYY_H_MM_A, true),
        invoiceStatus: offerListValuesSanitized.status
      };
    } else {
      return this.populateGenericTeamRedColumns(item);
    }
  }

  buildAcpColumnValues(item: AuctionItemSell) {
    if(this.isAcpAndSold(item)) {
      return {
        auctionItemStatus: item?.auctionItemStatus,
        soldToRepName: item?.acpAvisRepName
      };
    } else {
      return this.populateGenericTeamRedColumns(item);
    }
  }

  populateGenericTeamRedColumns(item: AuctionItemSell) {
    if(this.isIsvDetailsEnabled || this.isAcpDetailsEnabled) {
      if (this.isAuctionItemPresent(item)) {
        const itemStatus = this.isAuctionItemStatusPresent(item?.auctionItemStatus) ? item.auctionItemStatus : '';
        return { auctionItemStatus: itemStatus };
      } else {
        return { auctionItemStatus: '' };
      }
    } else {
      return {};
    }
  }

  isAuctionItemPresent(auctionItem: AuctionItemSell) {
    return auctionItem !== null && auctionItem !== undefined;
  }

  isAuctionItemStatusPresent(status: AuctionItemStatus) {
    return status !== null && status !== undefined;
  }

  getSanitizedReservationValues(item: AuctionItemSell): ReservationExportValues {
    const reservationExportValues = this.getDefaultReservationExportValues();
    const reservedByUserName = item?.reservation?.reservedByUserName;
    const reservedAt = item?.reservation?.reservedAt;

    if(this.isIsvAndReservedSoldOrInvoiced(item)) {
      if(reservedByUserName !== null && reservedByUserName !== undefined && reservedByUserName.length > 0) {
        reservationExportValues.reservedByUserName = reservedByUserName;
      }

      if(reservedAt !== null && reservedAt !== undefined && reservedAt.length > 0) {
        reservationExportValues.reservedAt = reservedAt;
      }
    }
    return reservationExportValues;
  }

  getDefaultReservationExportValues(): ReservationExportValues {
    return {
      reservedByUserName: '',
      reservedAt: ''
    } as ReservationExportValues;
  }

  getMostRecentInvoiceValuesFromAuctionItem(item: AuctionItemSell): OfferListExportValues {
    const offerListExportValues: OfferListExportValues = this.getOfferListDefaultValues();
    if(this.isOfferListsPresentOnAuctionItem(item)) {
      const mostRecentOfferListSanitized = this.findMostRecentOfferValues(item.offerLists);
      offerListExportValues.sentDate = mostRecentOfferListSanitized.sentDate;
      offerListExportValues.status = mostRecentOfferListSanitized.status;
    }
    return offerListExportValues;
  }

  isOfferListsPresentOnAuctionItem(item: AuctionItemSell): boolean {
    return item !== null && item !== undefined
            && item?.offerLists !== null && item?.offerLists !== undefined
            && item?.offerLists?.length > 0;
  }

  findMostRecentOfferValues(offerLists: OfferList[]): OfferListExportValues {
    if(offerLists !== null && offerLists !== undefined && offerLists?.length > 0) {
      let mostRecentOfferList = undefined;
      let mostRecentDate = undefined;

      if(offerLists.length === 1) {
        // If only one offer list, use that list
        mostRecentOfferList = offerLists[0];
      } else {
        // If multiple offer lists, find most recent
        offerLists.forEach(offerList => {
          if(offerList?.sentDate === null || offerList?.sentDate === undefined) {
            // Sanitizing the value to be used in new Date method
            offerList.sentDate = '';
          }

          if(!mostRecentDate || new Date(offerList.sentDate) > mostRecentDate) {
            mostRecentDate = new Date(offerList.sentDate);
            mostRecentOfferList = offerList;
          }
        });
      }
      return this.buildSanitizedOfferListExportValues(mostRecentOfferList.sentDate, mostRecentOfferList.status);
    } else {
      return this.getOfferListDefaultValues();
    }
  }

  buildSanitizedOfferListExportValues(sentDate: string, status: string): OfferListExportValues {
    const sanitizedDate = (sentDate !== null && sentDate !== undefined && sentDate.length > 0) ? sentDate: '';
    const sanitizedStatus = (status !== null && status !== undefined && status.length > 0) ? status : '';

    return {
      sentDate: sanitizedDate,
      status: sanitizedStatus
    } as OfferListExportValues;
  }

  getOfferListDefaultValues(): OfferListExportValues {
    return {
      sentDate: '',
      status: ''
    } as OfferListExportValues;
  }

  isIsvAndReservedSoldOrInvoiced(item: AuctionItemSell): boolean {
    return this.isIsvVehicle(item) && (this.isOfferListsPresentOnAuctionItem(item) || item.auctionItemStatus === 'RESERVED' || item.auctionItemStatus === 'SOLD');
  }

  isAcpAndSold(item: AuctionItemSell): boolean {
    return this.isAcpVehicle(item) && item.auctionItemStatus === soldStatus;
  }

  isIsvVehicle(item: AuctionItemSell): boolean {
    return item?.vehicleTypeCode === ISV_VEHICLE_TYPE;
  }

  isAcpVehicle(item: AuctionItemSell): boolean {
    return item?.vehicleTypeCode === ACP_VEHICLE_TYPE;
  }

  private assertColumnsSet(toCheck: string[], message: string) {
    const keys = this.finalColumns.map(({ key }) => key);
    const diff = _difference(keys, toCheck);
    console.assert(diff.length === 0, `${message}. Columns: ${diff.join(', ')}`);
  }

  private filterColumnsBasedOnPermissions() {
    if (this.columnsToFilter.length === 0) {
      return;
    }
    this.finalColumns = this.finalColumns.filter(x => !this.columnsToFilter.includes(x.key));
  }

  private filterRowBasedOnPermissions(defaults: Object, values: Object) {
    if (this.columnsToFilter.length === 0) {
      return { ...defaults, ...values };
    }
    return {
      ..._omit(defaults, this.columnsToFilter),
      ..._omit(values, this.columnsToFilter)
    };
  }

  private getOrigin() {
    return window.location.origin;
  }

  private vehicleInventoryLink(inventoryItemUuid) {
    return this.getOrigin() + `/ui/inventory/${inventoryItemUuid}`;
  }

  setAuctionTime(time, status) {
    return status === 'SOLD' ? undefined : this.dateUtilsService.formatDate(time, DateTimeFormat.MM_DD_YYYY_H_MM_A, true)
  }
}

export interface OfferListExportValues {
  sentDate: string;
  status: string;
}

export interface ReservationExportValues {
  reservedByUserName: string;
  reservedAt: string;
}
